import { useEffect, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Deal } from '@/types'
import { DealPanelTextarea } from './DealPanelTextarea'

const labelClass = 'text-[10px] font-medium uppercase tracking-wider text-text-label'

export function QualificacaoSection({
  deal,
  onDealPatched,
  required = false,
  embedded = false,
  deferSave = false,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
  required?: boolean
  embedded?: boolean
  deferSave?: boolean
  attention?: boolean
}) {
  const [observacao, setObservacao] = useState(deal.doresIdentificadas ?? '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const doresFromReuniao = deal.reunioes?.find((r) => r.notas?.trim())?.notas
    setObservacao(deal.doresIdentificadas ?? deal.leadProblema ?? doresFromReuniao ?? '')
  }, [deal.id, deal.doresIdentificadas, deal.leadProblema, deal.reunioes])

  const persist = async (value: string) => {
    if (deferSave) return
    setSaving(true)
    try {
      await api.patch(`/deals/${deal.id}`, { doresIdentificadas: value })
      onDealPatched({ doresIdentificadas: value })
    } catch {
      toast.error('Erro ao salvar observação')
    } finally {
      setSaving(false)
    }
  }

  const content = (
    <>
      {!embedded && (
        <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
          <CheckCircle2 size={14} />
          Observação da reunião
        </h3>
      )}

      <div className="flex flex-col gap-1.5">
        <label className={labelClass} htmlFor={`bant-obs-${deal.id}`}>
          Observação
          {required && <span className="text-warning"> *</span>}
        </label>
        <DealPanelTextarea
          id={`bant-obs-${deal.id}`}
          value={observacao}
          attention={required}
          invalid={required && !observacao.trim()}
          rows={4}
          placeholder="Anote se demonstrou interesse, o que foi combinado ou qualquer observação da reunião."
          aria-label="Observação da reunião"
          onChange={(next) => {
            setObservacao(next)
            onDealPatched({ doresIdentificadas: next })
          }}
          onBlur={() => { void persist(observacao) }}
        />
      </div>

      {saving && (
        <div className="flex items-center gap-2 text-xs text-text-label">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-accent/30 border-t-accent" />
          Salvando...
        </div>
      )}
    </>
  )

  if (embedded) {
    return <div className="space-y-3">{content}</div>
  }

  return <div className="glass-card space-y-3 p-4">{content}</div>
}
